import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoredTheme, setStoredTheme, THEMES, type Theme } from '../lib/theme.js';
import styles from './ThemeToggle.module.css';

const ICON: Record<Theme, string> = { dark: '🌙', light: '☀️' };

/**
 * The segmented dark/light control, shaped like `LanguageToggle` so the two
 * settings rows read as the same kind of choice.
 *
 * Applies immediately and stores the choice on this device — see
 * `lib/theme.ts` for why theme is a device preference rather than an account
 * one. Local state only mirrors what's already been written, so the pressed
 * state stays in step without a second source of truth.
 */
export default function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  return (
    <div className={styles.group} role="group" aria-label={t('settings.themeTitle')}>
      {THEMES.map((option) => {
        const isActive = theme === option;
        return (
          <button
            key={option}
            type="button"
            className={`${styles.option} ${isActive ? styles.active : ''}`}
            aria-pressed={isActive}
            onClick={() => {
              setStoredTheme(option);
              setTheme(option);
            }}
          >
            <span aria-hidden="true">{ICON[option]}</span>
            {t(`settings.theme.${option}`)}
          </button>
        );
      })}
    </div>
  );
}
